package com.crowpay.views.screens.auth

import com.crowpay.views.components.nav.AppDimensions
import com.crowpay.edit
import com.crowpay.extensions.fullName
import com.crowpay.sdk.notNullSession
import com.crowpay.sdk.selectedApi
import com.crowpay.utils.Action
import com.crowpay.utils.RestartableProcess
import com.crowpay.utils.pushChanges
import com.crowpay.utils.validation.Validator
import com.crowpay.views.components.activityIcon
import com.crowpay.views.components.label2
import com.crowpay.views.components.space
import com.crowpay.views.screens.client.ClientForm
import com.crowpay.views.theming.body
import com.crowpay.views.theming.bodyBold
import com.crowpay.views.theming.lightSection
import com.crowpay.views.theming.primaryButton
import com.crowpay.views.theming.secondaryButton
import com.crowpay.views.theming.specCenteredText
import com.crowpay.views.theming.tertiaryButton
import com.crowpay.views.theming.textButton
import com.crowpay.views.theming.title
import com.lightningkite.kiteui.Routable
import com.lightningkite.kiteui.models.Align
import com.lightningkite.kiteui.models.Icon
import com.lightningkite.kiteui.models.dp
import com.lightningkite.kiteui.navigation.Page
import com.lightningkite.kiteui.navigation.render
import com.lightningkite.kiteui.reactive.Constant
import com.lightningkite.kiteui.reactive.LazyProperty
import com.lightningkite.kiteui.reactive.Property
import com.lightningkite.kiteui.reactive.Readable
import com.lightningkite.kiteui.reactive.bind
import com.lightningkite.kiteui.reactive.invoke
import com.lightningkite.kiteui.reactive.shared
import com.lightningkite.kiteui.views.ViewModifiable
import com.lightningkite.kiteui.views.ViewWriter
import com.lightningkite.kiteui.views.atEnd
import com.lightningkite.kiteui.views.centered
import com.lightningkite.kiteui.views.compact
import com.lightningkite.kiteui.views.direct.PhoneNumberFormat
import com.lightningkite.kiteui.views.direct.button
import com.lightningkite.kiteui.views.direct.col
import com.lightningkite.kiteui.views.direct.gravity
import com.lightningkite.kiteui.views.direct.onClick
import com.lightningkite.kiteui.views.direct.onlyWhen
import com.lightningkite.kiteui.views.direct.padded
import com.lightningkite.kiteui.views.direct.row
import com.lightningkite.kiteui.views.direct.scrolls
import com.lightningkite.kiteui.views.direct.sizeConstraints
import com.lightningkite.kiteui.views.direct.textInput
import com.lightningkite.kiteui.views.expanding
import com.lightningkite.kiteui.views.fieldTheme
import com.lightningkite.kiteui.views.l2.icon
import com.lightningkite.lightningserver.auth.proof.FinishProof
import kotlinx.coroutines.delay

@Routable("user/profile")
class UserProfile : Page, Validator() {

    override val title: Readable<String> = Constant("Profile")

    val cached = shared {
        val session = notNullSession()
        session.users.watch(session.userId)
    }

    val self = LazyProperty {
        cached()()!!
    }

    val editingUser = Property(false)

    val clientForm = ClientForm(self, AppDimensions.pageWidth)

    override fun ViewWriter.render2(): ViewModifiable {
        return scrolls - col {
            spacing = 0.dp

            title("Profile")

            space(AppDimensions.backgroundIndent)

            sizeConstraints(width = AppDimensions.pageWidth) - padded - row {
                spacing = 0.dp
                space(AppDimensions.fullIndent)

                expanding - col {
                    onlyWhen { !editingUser() } - col {

                        label2("Legal Name") {
                            body {
                                ::content{ self().fullName }
                            }
                        }

                        label2("Email") {
                            body {
                                ::content{ self().email.raw }
                            }
                        }

                        label2("Phone Number") {
                            body {
                                ::content { PhoneNumberFormat.USA.format(self().phoneNumber.raw.filter { it.isDigit() }.takeLast(10)) }
                            }
                        }

                        onlyWhen { !editingUser() && !self().phoneVerified } - lightSection - phoneNumberVerification()

                        label2("Address") {
                            body {
                                ::content{ self().address.toString() }
                            }
                        }
                        tertiaryButton - button {
                            icon(Icon.Companion.edit, "Edit Profile")
                            onClick {
                                editingUser.set(true)
                            }
                        } in gravity(Align.End, Align.End)

                    }

                    onlyWhen { editingUser() } - col {

                        clientForm.render(this@col)

                        row {
                            spacing = AppDimensions.buttonRowSpacing
                            secondaryButton - button {
                                specCenteredText("Cancel")
                                onClick {
                                    editingUser.set(false)
                                    clientForm.user.cancel()
                                }
                            }

                            primaryButton - button {
                                ::enabled{ clientForm.allValid() }
                                specCenteredText("Save")
                                onClick {
                                    clientForm.user.pushChanges(notNullSession().users)
                                    editingUser.set(false)
                                }
                            }
                        } in gravity(Align.End, Align.End)
                    }
                }
                space(AppDimensions.fullIndent)
            }
        }
    }

//    private val resendCooldown = RestartableProcess {
//        var count = 15
//
//    }
    private val token = Property<String?>(null)

    private val pinChars = ('A'..'Z').toList() - setOf('I', 'O')
    private val passcode = Property("").validate { str -> (str.length == 6) and (str.all { it in pinChars }) }

    private val confirmation = Property(false)
    private val cooldown = RestartableProcess {
        var count = 10
        while (count > 0) {
            emit(count--)
            if (count < 7) confirmation.value = false
            delay(1000)
        }
        emit(null)
    }

    private val verifyPhoneNumber = Action("Verify Phone Number") {
        if (!passcode.valid()) throw IllegalStateException("Invalid Passcode")
        val token = token() ?: throw IllegalStateException("No proof token")

        val session = notNullSession()
        val proof = session.nonCached.api.smsProof.provePhoneOwnership(FinishProof(token, passcode()))
        session.nonCached.user.verifyPhoneNumber(proof)
    }

    private fun ViewWriter.phoneNumberVerification() =
        col {
            compact - row {
//                centered - icon(Icon.activity, "action needed")
                centered - activityIcon()
                centered - bodyBold("Verify Phone Number")
            }

            body {
                ::content {
                    if (token() == null) "Your phone number needs to be verified before you can receive SMS notifications."
                    else "Please enter the passcode texted to ${self().phoneNumber} below to verify your phone number."
                }
            }

            onlyWhen { token() != null } - col {
                fieldTheme - textInput {
                    hint = "Passcode"
                    content bind passcode
                    action = verifyPhoneNumber
                }
                centered - textButton - button {
                    ::enabled { cooldown() == null }
                    body {
                        ::content {
                            if (confirmation()) "Text sent!"
                            else cooldown()
                                ?.let { "Can resend code in $it" }
                                ?: "Send me a new code"
                        }
                    }
                    onClick("Send Code") {
                        token.value = selectedApi().api.smsProof.beginSmsOwnershipProof(self().phoneNumber.raw)
                        confirmation.value = true
                        cooldown.restart()
                    }
                }
            }

            atEnd - primaryButton - button {
                ::enabled { token() == null || passcode.valid() }
                specCenteredText {
                    if (token() == null) "Text Verification Code"
                    else "Submit"
                }
                onClick("Begin or Submit") {
                    val session = notNullSession()
                    val t = token()
                    if (t == null) {
                        token.value = session.nonCached.api.smsProof.beginSmsOwnershipProof(self().phoneNumber.raw)
                        cooldown.restart()
                    }
                    else verifyPhoneNumber.startAction(this)
                }
            }
        }

}